/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  Badge,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { ArrowBackIcon, AddIcon } from '@chakra-ui/icons';
import ReusableTable from '../../../components/ReusableTable';
import DoctorForm from './doctorForm';
import PlanSelectionModal from './PlanSelectionModal';
import { useDoctorList } from './hooks/useDoctorList';
import { useQueryClient } from '@tanstack/react-query';
import { useToggleUserStatus } from './hooks/useToggleUserStatus';
import useDebounce from 'constants/helper/useDebounce';

function DoctorUser() {
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(1);
  const [searchString, setSearchString] = useState('');
  const debouncedSearch = useDebounce(searchString, 500);
  const [selectedId, setSelectedId] = useState(null);

  // Plan Selection State
  const [isPlanModalOpen, setPlanModalOpen] = useState(false);
  const [activationId, setActivationId] = useState(null);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [currentDoctorStatus, setCurrentDoctorStatus] = useState(false);

  const { doctors, totalPages, isLoading } = useDoctorList(
    page,
    debouncedSearch,
  );

  const { mutate: toggleStatus, isLoading: toggleLoading } =
    useToggleUserStatus();
  const formatDate = (date) => {
    if (!date) return '-';

    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };
  const tableData = doctors?.map((item, index) => ({
    ...item,
    srNo: (page - 1) * 20 + index + 1,
    endDate: formatDate(item.endDate),
  }));

  const handleSearch = (value) => {
    setSearchString(value);
  };

  const handleToggleStatus = (id) => {
    // Always Open Plan Selection
    const targetId = typeof id === 'object' ? id._id || id.id : id;
    const doctor = doctors?.find((doc) => doc._id === targetId || doc.id === targetId);
    
    setActivationId(targetId);
    setCurrentPlanId(doctor?.planId || null);
    setCurrentDoctorStatus(doctor?.isActive || false);
    setPlanModalOpen(true);
  };

  const handlePlanSubmit = (selectedPlan) => {
    const today = new Date();

    const durationYears = selectedPlan?.durationYears || 1;

    const endDate = new Date();
    endDate.setFullYear(today.getFullYear() + durationYears);

    const formatDate = (date) => date.toISOString().split('T')[0];

    const payload = {
      id: activationId,
      planId: selectedPlan._id,
      planName: selectedPlan.name,
      planActivationCode: selectedPlan.planActivationCode,
      planDuration:
        selectedPlan.planCode === '1Y'
          ? '1 Year'
          : selectedPlan.planCode === '6M'
            ? '6 Months'
            : '3 Months',
      startDate: formatDate(today),
      endDate: formatDate(endDate),
    };

    toggleStatus(payload, {
      onSuccess: () => {
        setPlanModalOpen(false);
        setActivationId(null);
      },
    });
  };

  return (
    <Box p={4} mt={10}>
      {!showForm && (
        <ReusableTable
          data={tableData}
          columns={[
            { key: 'srNo', label: 'Sr.No', width: '80px', headerSize: '14px' },
            { key: 'userId', label: 'Practitioners ID', width: '120px' },
            { key: 'name', label: 'Name', width: '200px' },
            { key: 'email', label: 'Email', width: '220px' },
            { key: 'mobile', label: 'Mobile', width: '140px' },
            { key: 'endDate', label: 'Plan Expiry Date', width: '140px' },
            {
              key: 'isActive',
              label: 'Status',
              width: '100px',
              render: (value) => (
                <Badge colorScheme={value ? 'green' : 'red'}>
                  {value ? 'Active' : 'Inactive'}
                </Badge>
              ),
            },
          ]}
          page={page}
          totalPages={totalPages}
          loading={isLoading}
          onPageChange={(p) => setPage(p)}
          onToggleActive={handleToggleStatus}
          showTopAction
          showAddBtn={false}
          topAction={{
            title: 'Practitioners Management',
            label: 'Add Doctor',
            icon: <AddIcon />,
            onClick: () => setShowForm(true),
          }}
          showViewToggle={false}
          showSearch={true}
          onSearch={handleSearch}
        />
      )}

      {/* ===== FORM VIEW ===== */}
      {showForm && (
        <Box>
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <Text fontSize="22px" fontWeight="bold">
              Add Doctor
            </Text>

            <Button
              variant="ghost"
              leftIcon={<ArrowBackIcon />}
              onClick={() => setShowForm(false)}
            >
              Back
            </Button>
          </Flex>

          <DoctorForm
            // editId={selectedId}
            onSuccess={() => {
              setShowForm(false);
            }}
          />
        </Box>
      )}

      <PlanSelectionModal
        isOpen={isPlanModalOpen}
        onClose={() => {
          setPlanModalOpen(false);
          setActivationId(null);
          setCurrentPlanId(null);
          setCurrentDoctorStatus(false);
        }}
        onSubmit={handlePlanSubmit}
        isLoading={toggleLoading}
        defaultPlanId={currentPlanId}
        isActive={currentDoctorStatus}
      />
    </Box>
  );
}

export default DoctorUser;
